import { ORDER_STATUSES, type OrderStatus } from '@ambernord/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Skeleton,
  StatCard,
  StatusBadge,
  cn,
} from '@ambernord/ui';
import {
  AlarmClock,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  FileWarning,
  Inbox,
  Package,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

/**
 * Rīcības-orientēts dashboard: KPI josla + "vajadzīga jūsu rīcība" + trūkstošie
 * artefakti + to-do. Datu avots: GET /api/dashboard (tenant-scoped, lomas tvērums).
 * §7: piegādātāja/pārvadātāja tvērumā API nekad neatdod gala klienta identitāti —
 * šeit renderējam TIKAI atdotos laukus.
 */

type DashboardScope = 'platform' | 'supplier' | 'carrier' | 'customer';

interface CurrentItem {
  id: string;
  ref: string;
  status: OrderStatus;
  milestone: string;
  shippingMode: string;
  deliveryWindowStart: string | null;
  deliveryWindowEnd: string | null;
  routeOrigin: string | null;
  routeDestination: string | null;
  carrierTenantId: string | null;
  /** Tikai platformas tvērumā (§7). */
  endCustomerOrderId?: string;
}

interface TodoItem {
  id: string;
  type: string;
  title: string;
  status: string;
  dueAt: string | null;
  relatedEntity: string | null;
  relatedId: string | null;
}

interface MissingArtifact {
  supplyOrderId: string;
  ref: string;
  artifact: string;
  forStatus: OrderStatus;
}

interface DeadlineItem {
  label: string;
  entity: string;
  entityId: string;
  dueAt: string;
  msRemaining: number;
  daysRemaining: number;
  overdue: boolean;
}

interface DashboardResponse {
  scope: DashboardScope;
  generatedAt: string;
  currentItems: CurrentItem[];
  todos: TodoItem[];
  missingArtifacts: MissingArtifact[];
  deadlines: DeadlineItem[];
  counts: { currentItems: number; todos: number; missingArtifacts: number; overdue: number };
  error?: string;
}

const MAX_ACTION_ROWS = 8;
const MAX_TODO_ROWS = 6;

function greetingKey(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export default function Dashboard() {
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await api.get<DashboardResponse>('/api/dashboard');
    if (res.ok && !res.data.error) {
      setData(res.data);
    } else {
      setFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const scope = data?.scope ?? 'customer';
  // Klients redz savus pasūtījumus /orders; pārējie tvērumi — piegādes pasūtījumus.
  const listPath = scope === 'customer' ? '/orders' : '/supply-orders';

  /** Agrākais termiņš katram objektam (API atdod augoši sakārtotus). */
  const deadlineByEntity = useMemo(() => {
    const map = new Map<string, DeadlineItem>();
    for (const d of data?.deadlines ?? []) {
      if (!map.has(d.entityId)) map.set(d.entityId, d);
    }
    return map;
  }, [data]);

  const itemsById = useMemo(() => {
    const map = new Map<string, CurrentItem>();
    for (const item of data?.currentItems ?? []) map.set(item.id, item);
    return map;
  }, [data]);

  const statusLabel = (status: string) => t(`statuses:${status}`, { defaultValue: status });

  const renderCountdown = (d: DeadlineItem) => {
    if (d.overdue) {
      return (
        <span className="shrink-0 text-xs font-semibold text-red-600 dark:text-red-400">
          {t('deadline.overdue')}
        </span>
      );
    }
    return (
      <span
        className={cn(
          'shrink-0 text-xs font-medium',
          d.daysRemaining <= 2
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-slate-500 dark:text-slate-400',
        )}
      >
        {d.daysRemaining <= 0
          ? t('deadline.dueToday')
          : t('deadline.dueIn', { count: d.daysRemaining })}
      </span>
    );
  };

  if (loading) {
    return (
      <AppLayout title={t('title')}>
        <div className="space-y-6" aria-busy="true">
          <div className="space-y-2">
            <Skeleton className="h-6 w-52" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-[74px]" />
            <Skeleton className="h-[74px]" />
            <Skeleton className="h-[74px]" />
            <Skeleton className="h-[74px]" />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <Skeleton className="h-72 lg:col-span-2" />
            <div className="space-y-6">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (failed || !data) {
    return (
      <AppLayout title={t('title')}>
        <div className="space-y-4">
          <DataState loading={false} error={t('error')} empty={false}>
            {null}
          </DataState>
          <Button variant="secondary" onClick={() => void load()}>
            {t('retry')}
          </Button>
        </div>
      </AppLayout>
    );
  }

  const { counts } = data;
  const actionItems = data.currentItems.slice(0, MAX_ACTION_ROWS);
  const todoItems = data.todos.slice(0, MAX_TODO_ROWS);
  const now = Date.now();

  return (
    <AppLayout title={t('title')}>
      {/* Sveiciens + neuzkrītošs konta marķieris */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t(`greeting.${greetingKey(new Date().getHours())}`)}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{t('subtitle')}</p>
        </div>
        {user ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span>{user.email}</span>
            <Badge tone="blue">{user.role}</Badge>
          </div>
        ) : null}
      </div>

      {/* KPI josla */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('kpi.currentOrders')}
          value={counts.currentItems}
          icon={<Package className="h-5 w-5" />}
          onClick={() => navigate(listPath)}
        />
        <StatCard
          label={t('kpi.actionRequired')}
          value={counts.todos}
          icon={<ClipboardList className="h-5 w-5" />}
          onClick={() => navigate('/tasks')}
        />
        <StatCard
          label={t('kpi.missingDocuments')}
          value={counts.missingArtifacts}
          tone={counts.missingArtifacts > 0 ? 'danger' : 'default'}
          icon={<FileWarning className="h-5 w-5" />}
          onClick={() =>
            navigate(scope === 'platform' || scope === 'supplier' ? '/documents' : listPath)
          }
        />
        <StatCard
          label={t('kpi.overdue')}
          value={counts.overdue}
          tone={counts.overdue > 0 ? 'warn' : 'default'}
          icon={<AlarmClock className="h-5 w-5" />}
          onClick={() => navigate('/tasks')}
        />
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        {/* Vajadzīga jūsu rīcība — kārtējie pasūtījumi */}
        <Card className="lg:col-span-2">
          <CardHeader
            title={t('action.heading')}
            subtitle={t('action.subheading')}
            action={
              <Link
                to={listPath}
                className="text-xs font-semibold text-brand-600 hover:underline dark:text-brand-400"
              >
                {t('viewAll')}
              </Link>
            }
          />
          <CardBody>
            {actionItems.length === 0 ? (
              <EmptyState
                icon={<CheckCircle2 className="h-6 w-6" />}
                title={t('action.empty')}
                hint={t('action.emptyHint')}
              />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {actionItems.map((item) => {
                  const deadline = deadlineByEntity.get(item.id);
                  const to = scope === 'customer' ? '/orders' : `/supply-orders/${item.id}`;
                  return (
                    <li key={item.id}>
                      <Link to={to} className="group flex items-center gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">
                              {item.ref}
                            </span>
                            <StatusBadge status={item.status} label={statusLabel(item.status)} />
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                            <span>
                              {t(`shippingMode.${item.shippingMode}`, {
                                defaultValue: item.shippingMode,
                              })}
                            </span>
                            {item.routeOrigin && item.routeDestination ? (
                              <span>
                                {item.routeOrigin} → {item.routeDestination}
                              </span>
                            ) : null}
                            {item.deliveryWindowStart ? (
                              <span>
                                {formatDate(item.deliveryWindowStart, lang)}
                                {item.deliveryWindowEnd
                                  ? ` – ${formatDate(item.deliveryWindowEnd, lang)}`
                                  : ''}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {deadline ? renderCountdown(deadline) : null}
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        <div className="space-y-6">
          {/* Trūkstošie artefakti — dzintarkrāsas/sarkani brīdinājumi */}
          <Card>
            <CardHeader title={t('missing.heading')} subtitle={t('missing.subheading')} />
            <CardBody className="space-y-2">
              {data.missingArtifacts.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 className="h-6 w-6" />}
                  title={t('missing.empty')}
                  hint={t('missing.emptyHint')}
                  className="py-8"
                />
              ) : (
                data.missingArtifacts.map((m) => {
                  const order = itemsById.get(m.supplyOrderId);
                  // Sarkans = pasūtījums jau PĀRI solim, kuram artefakts obligāts (bloķējošs robs).
                  const severe =
                    order !== undefined &&
                    ORDER_STATUSES.indexOf(order.status) > ORDER_STATUSES.indexOf(m.forStatus);
                  return (
                    <Link
                      key={`${m.supplyOrderId}-${m.artifact}`}
                      to={`/supply-orders/${m.supplyOrderId}`}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                        severe
                          ? 'border-red-200 bg-red-50 text-red-800 hover:border-red-300 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300'
                          : 'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300',
                      )}
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1">
                        {t('missing.item', {
                          ref: m.ref,
                          artifact: t(`artifacts.${m.artifact}`, { defaultValue: m.artifact }),
                        })}
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  );
                })
              )}
            </CardBody>
          </Card>

          {/* To-do uzdevumi */}
          <Card>
            <CardHeader
              title={t('todos.heading')}
              subtitle={t('todos.subheading')}
              action={
                <Link
                  to="/tasks"
                  className="text-xs font-semibold text-brand-600 hover:underline dark:text-brand-400"
                >
                  {t('viewAll')}
                </Link>
              }
            />
            <CardBody>
              {todoItems.length === 0 ? (
                <EmptyState
                  icon={<Inbox className="h-6 w-6" />}
                  title={t('todos.empty')}
                  hint={t('todos.emptyHint')}
                  className="py-8"
                />
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {todoItems.map((todo) => {
                    const overdue = todo.dueAt !== null && new Date(todo.dueAt).getTime() < now;
                    return (
                      <li key={todo.id}>
                        <Link to="/tasks" className="group flex items-center gap-3 py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                              {todo.title}
                            </p>
                            {todo.dueAt ? (
                              <p
                                className={cn(
                                  'mt-0.5 text-xs',
                                  overdue
                                    ? 'font-semibold text-red-600 dark:text-red-400'
                                    : 'text-slate-500 dark:text-slate-400',
                                )}
                              >
                                {t('todos.due', { date: formatDate(todo.dueAt, lang) })}
                              </p>
                            ) : null}
                          </div>
                          <StatusBadge status={todo.status} label={statusLabel(todo.status)} />
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
